/**
 * VixoKart Theme — Cart Drawer (Phase 6)
 * Handles: open/close, fetch cart state, update quantities,
 * remove items, discount code, free shipping progress, upsell (2–4 products).
 *
 * Item mutations use /cart/change.js with `id` (the item key),
 * which is stable regardless of position — avoids index-drift bugs.
 */

'use strict';

VixoKart.Cart = (function() {

  // ─── State ──────────────────────────────────────────────────────────────────

  let cartData = null;
  const pendingKeys = new Set();
  const quantityTimers = new Map();

  // ─── DOM refs ────────────────────────────────────────────────────────────────

  const drawer  = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-overlay');

  if (!drawer) return null;

  const countBadges = document.querySelectorAll('[data-cart-count]');
  const body        = drawer.querySelector('.cart-drawer__body');
  const itemsWrap   = drawer.querySelector('.cart-drawer__items');
  const emptyWrap   = drawer.querySelector('.cart-empty');
  const emptyRecs   = drawer.querySelector('#cart-empty-recs');
  const emptyRecsGrid = drawer.querySelector('#cart-empty-recs-grid');
  const footer      = drawer.querySelector('.cart-drawer__footer');
  const subtotalEl  = drawer.querySelector('[data-cart-subtotal]');
  const countTitle  = drawer.querySelector('.cart-drawer__count');
  const freeBar     = drawer.querySelector('.cart-shipping-bar__fill');
  const freeTrack   = drawer.querySelector('.cart-shipping-bar__track');
  const freeMsg     = drawer.querySelector('.cart-shipping-bar__message');

  // ─── Open / Close ────────────────────────────────────────────────────────────

  function open() {
    drawer.classList.add('active');
    drawer.setAttribute('aria-hidden', 'false');
    overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
    // Hide site header while cart is open
    const header = document.getElementById('site-header');
    if (header) {
      header.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
      header.style.transform  = 'translateY(-100%)';
      header.style.opacity    = '0';
      header.style.pointerEvents = 'none';
      header.setAttribute('inert', '');
    }
    // Hide mobile bottom nav while cart is open
    const mobileNav = document.querySelector('.mobile-bottom-nav');
    if (mobileNav) {
      mobileNav.style.transition    = 'transform 0.3s ease, opacity 0.3s ease';
      mobileNav.style.transform     = 'translateY(100%)';
      mobileNav.style.opacity       = '0';
      mobileNav.style.pointerEvents = 'none';
      mobileNav.setAttribute('inert', '');
    }
    // Focus back button for accessibility
    requestAnimationFrame(() => {
      drawer.querySelector('[data-cart-close]')?.focus();
    });
  }

  function close() {
    drawer.classList.remove('active');
    drawer.setAttribute('aria-hidden', 'true');
    overlay?.classList.remove('active');
    document.body.style.overflow = '';
    // Restore site header
    const header = document.getElementById('site-header');
    if (header) {
      header.style.transform     = '';
      header.style.opacity       = '';
      header.style.pointerEvents = '';
      header.removeAttribute('inert');
    }
    // Restore mobile bottom nav
    const mobileNav = document.querySelector('.mobile-bottom-nav');
    if (mobileNav) {
      mobileNav.style.transform     = '';
      mobileNav.style.opacity       = '';
      mobileNav.style.pointerEvents = '';
      mobileNav.removeAttribute('inert');
    }
  }

  // ─── Fetch cart ───────────────────────────────────────────────────────────────

  async function fetchCart() {
    const data = await VixoKart.fetchJSON(window.routes.cart_url + '.js');
    cartData = data;
    return data;
  }

  // ─── Render items ─────────────────────────────────────────────────────────────

  function renderCartItems(cart) {
    if (!itemsWrap) return;

    if (cart.item_count === 0) {
      itemsWrap.innerHTML = '';
      emptyWrap?.classList.remove('hidden');
      emptyWrap?.removeAttribute('aria-hidden');
      footer?.classList.add('hidden');
      if (countTitle) countTitle.textContent = '0';
      // Render empty-state recommendations
      renderEmptyRecs(cart);
      return;
    }

    emptyWrap?.classList.add('hidden');
    emptyWrap?.setAttribute('aria-hidden', 'true');
    footer?.classList.remove('hidden');
    if (countTitle) countTitle.textContent = cart.item_count;

    itemsWrap.innerHTML = cart.items.map(item => {
      const rule = getQuantityRule(item);
      const onSale        = item.original_price !== item.final_price;
      const linePrice     = VixoKart.formatMoney(item.line_price);
      const origLinePrice = VixoKart.formatMoney(item.original_line_price);
      const unitPrice     = VixoKart.formatMoney(item.price);
      const imgSrc        = item.featured_image?.url
        ? item.featured_image.url.split('?')[0] + '?width=160'
        : '';

      return `
        <div class="cart-item" data-item-key="${item.key}">
          <a class="cart-item__img-wrap" href="${item.url}" tabindex="-1" aria-label="${escHtml(item.title)}">
            ${imgSrc
              ? `<img class="cart-item__img" src="${imgSrc}" alt="${escHtml(item.title)}" width="80" height="100" loading="lazy">`
              : `<div class="cart-item__img-placeholder">
                   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="28" height="28" aria-hidden="true">
                     <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                   </svg>
                 </div>`
            }
          </a>
          <div class="cart-item__details">
            <a class="cart-item__title" href="${item.url}">${escHtml(item.product_title)}</a>
            ${item.variant_title && item.variant_title !== 'Default Title'
              ? `<div class="cart-item__variant">${escHtml(item.variant_title)}</div>`
              : ''}
            <div class="cart-item__bottom">
              <div class="quantity-selector" data-item-key="${item.key}">
                <button class="quantity-btn" data-step="-1" type="button" aria-label="Decrease quantity">−</button>
                <input
                  class="quantity-input"
                  type="number"
                  value="${item.quantity}"
                  min="${rule.min}"
                  ${rule.max !== null ? `max="${rule.max}"` : ''}
                  step="${rule.step}"
                  inputmode="numeric"
                  aria-label="Quantity for ${escHtml(item.product_title)}"
                  data-item-key="${item.key}"
                >
                <button class="quantity-btn" data-step="1" type="button" aria-label="Increase quantity">+</button>
              </div>
              <p class="cart-item__error" role="alert" aria-live="polite" hidden></p>
              <div class="cart-item__price-remove">
                <div class="cart-item__price-wrap">
                  ${onSale
                    ? `<s class="cart-item__compare">${origLinePrice}</s>
                       <span class="cart-item__price sale">${linePrice}</span>`
                    : `<span class="cart-item__price">${linePrice}</span>`}
                  ${item.quantity > 1
                    ? `<span class="cart-item__unit">${unitPrice} each</span>`
                    : ''}
                </div>
                <button class="cart-item__remove" data-remove-key="${item.key}" type="button" aria-label="Remove ${escHtml(item.title)}">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
                    <path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      `;
    }).join('');

    bindItemEvents();
  }

  function bindItemEvents() {
    // Match Dawn's quantity behavior: validate integer values, disable the
    // controls while the request is pending, and restore the previous value
    // when Shopify rejects an update.
    itemsWrap.querySelectorAll('.quantity-input').forEach(input => {
      const key = input.dataset.itemKey;
      syncQuantityButtons(input.closest('.quantity-selector'), parseInt(input.value, 10) || 1, getQuantityRuleByKey(key));

      input.addEventListener('change', async () => {
        const key = input.dataset.itemKey;
        const oldQty = parseInt(input.defaultValue, 10) || 0;
        const qty = Number(input.value);
        const rule = getQuantityRuleByKey(key);
        if (!key || !Number.isInteger(qty) || !isValidQuantity(qty, rule)) {
          input.value = oldQty;
          showQuantityError(input, quantityError(rule));
          return;
        }
        scheduleQuantityUpdate(key, qty, input, oldQty);
      });
    });

    // Quantity +/− buttons
    itemsWrap.querySelectorAll('.quantity-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const wrap  = btn.closest('.quantity-selector');
        const input = wrap?.querySelector('.quantity-input');
        const key   = wrap?.dataset.itemKey;
        if (!key || !input) return;
        if (pendingKeys.has(key)) return;
        const step   = btn.dataset.step === '-1' ? -1 : 1;
        const curQty = parseInt(input.value, 10) || 1;
        const rule   = getQuantityRuleByKey(key);

        // Pressing minus at qty 1 removes the item from cart
        if (step === -1 && curQty <= 1) {
          const cartItem = btn.closest('.cart-item');
          if (cartItem) {
            cartItem.style.transition    = 'opacity 0.25s ease';
            cartItem.style.opacity       = '0.4';
            cartItem.style.pointerEvents = 'none';
          }
          scheduleQuantityUpdate(key, 0, input, curQty);
          return;
        }

        const newQty = curQty + (step * rule.step);
        if (!isValidQuantity(newQty, rule)) {
          showQuantityError(input, quantityError(rule));
          return;
        }
        input.value  = newQty;
        syncQuantityButtons(wrap, newQty, rule);
        scheduleQuantityUpdate(key, newQty, input, curQty);
      });
    });

    // Remove buttons
    itemsWrap.querySelectorAll('[data-remove-key]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.removeKey;
        if (!key) return;
        const item = btn.closest('.cart-item');
        if (item) {
          item.style.opacity    = '0.4';
          item.style.transition = 'opacity 0.25s ease';
          item.style.pointerEvents = 'none';
        }
        try {
          await changeItem(key, 0);
        } catch (err) {
          item?.style.removeProperty('opacity');
          item?.style.removeProperty('pointer-events');
          VixoKart.toast(err?.message || 'Could not remove item.', 'error');
        }
      });
    });
  }

  // Disables the +/− buttons once the input hits its min/max, mirroring
  // Dawn's <quantity-input> validateQtyRules(). Minus-at-min removes the item
  // instead of disabling (handled separately in the click handler above), so
  // this only disables minus once the rule's min is otherwise unreachable and
  // disables plus once the rule's max is hit.
  function syncQuantityButtons(wrap, quantity, rule) {
    if (!wrap) return;
    const minusBtn = wrap.querySelector('[data-step="-1"]');
    const plusBtn  = wrap.querySelector('[data-step="1"]');
    if (plusBtn) plusBtn.disabled = rule.max !== null && quantity >= rule.max;
    // Minus is never truly disabled at qty 1 — pressing it removes the item —
    // but once it would go below the configured min via steps, hold it.
    if (minusBtn) minusBtn.disabled = quantity <= rule.min && rule.min > 1;
  }

  function getQuantityRule(item) {
    return getQuantityRuleByKey(item.key);
  }

  function getQuantityRuleByKey(key) {
    const configured = window.cartQuantityRules?.[key] || {};
    return {
      min: Number(configured.min) > 0 ? Number(configured.min) : 1,
      max: configured.max === null || configured.max === undefined || configured.max === ''
        ? null
        : Number(configured.max),
      step: Number(configured.step) > 0 ? Number(configured.step) : 1
    };
  }

  function isValidQuantity(quantity, rule) {
    if (!Number.isInteger(quantity) || quantity < rule.min) return false;
    if (rule.max !== null && quantity > rule.max) return false;
    return (quantity - rule.min) % rule.step === 0;
  }

  function quantityError(rule) {
    if (rule.max !== null) return `Choose between ${rule.min} and ${rule.max} items in steps of ${rule.step}.`;
    return `Choose at least ${rule.min} item${rule.min === 1 ? '' : 's'} in steps of ${rule.step}.`;
  }

  // Dawn debounces quantity changes. This is important for rapid +/− clicks:
  // Shopify receives the final intended quantity instead of several requests
  // racing each other and repainting the drawer out of order.
  function scheduleQuantityUpdate(key, quantity, input, previousQuantity) {
    const existingTimer = quantityTimers.get(key);
    if (existingTimer) clearTimeout(existingTimer);
    clearQuantityError(input);
    quantityTimers.set(key, setTimeout(() => {
      quantityTimers.delete(key);
      updateQuantity(key, quantity, input, previousQuantity);
    }, 300));
  }

  function showQuantityError(input, message) {
    const item = input.closest('.cart-item');
    const error = item?.querySelector('.cart-item__error');
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
    input.setAttribute('aria-invalid', 'true');
  }

  function clearQuantityError(input) {
    const item = input.closest('.cart-item');
    const error = item?.querySelector('.cart-item__error');
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }
    input.removeAttribute('aria-invalid');
  }

  async function updateQuantity(key, quantity, input, previousQuantity) {
    if (pendingKeys.has(key)) return;
    pendingKeys.add(key);
    const selector = input.closest('.quantity-selector');
    const controls = selector?.querySelectorAll('button');
    controls?.forEach(control => { control.disabled = true; });
    input.disabled = true;
    clearQuantityError(input);

    try {
      await changeItem(key, quantity);
    } catch (err) {
      input.value = previousQuantity;
      showQuantityError(input, err?.message || 'Could not update quantity.');
    } finally {
      pendingKeys.delete(key);
      // The cart re-render normally replaces this input. Guard the reference
      // so an error response cannot throw if the item disappeared.
      if (input.isConnected) {
        input.disabled = false;
        controls?.forEach(control => { control.disabled = false; });
      }
    }
  }

  // ─── Subtotal & count ─────────────────────────────────────────────────────────

  function renderSubtotal(cart) {
    if (subtotalEl) subtotalEl.textContent = VixoKart.formatMoney(cart.total_price);
  }

  function renderCartCount(cart) {
    countBadges.forEach(badge => {
      badge.textContent = cart.item_count;
      badge.classList.toggle('hidden', cart.item_count === 0);
      badge.classList.remove('bump');
      void badge.offsetWidth;
      badge.classList.add('bump');
    });
  }

  // ─── Free shipping bar ────────────────────────────────────────────────────────

  function renderFreeShipping(cart) {
    const threshold = window.cartSettings?.freeShippingThreshold || 0;
    if (!threshold || !freeBar || !freeMsg) return;

    const spent   = cart.total_price;
    const percent = Math.min(100, (spent / threshold) * 100);
    freeBar.style.width = percent + '%';
    if (freeTrack) freeTrack.setAttribute('aria-valuenow', Math.round(percent));

    if (spent >= threshold) {
      const reachedText = window.cartStrings?.freeShippingReached || "You've unlocked free shipping!";
      freeMsg.innerHTML = `🎉 <strong>${reachedText}</strong>`;
      freeBar.style.background = 'var(--color-success, #22c55e)';
    } else {
      const remaining = VixoKart.formatMoney(threshold - spent);
      const template = window.cartStrings?.freeShippingRemaining || 'Add [amount] more for free shipping';
      freeMsg.innerHTML = template.replace('[amount]', `<strong>${remaining}</strong>`);
      freeBar.style.background = '';
    }
  }

  // ─── Empty-state recs ─────────────────────────────────────────────────────────

  async function renderEmptyRecs() {
    if (!emptyRecsGrid) return;
    try {
      // Use first product from shop as seed when cart is empty
      const shopUrl = window.routes.root_url || '/';
      const res  = await fetch(`${shopUrl}recommendations/products.json?product_id=&limit=4&intent=related`);
      // If no product_id, Shopify returns 422 — just hide the section gracefully
      if (!res.ok) { emptyRecs?.classList.add('hidden'); return; }
      const data = await res.json();
      const recs = (data.products || []).slice(0, 4);
      if (!recs.length) { emptyRecs?.classList.add('hidden'); return; }

      emptyRecsGrid.innerHTML = recs.map(rec => {
        const imgSrc = rec.featured_image
          ? rec.featured_image.split('?')[0] + '?width=120'
          : '';
        return `
          <a class="cart-empty-rec" href="${rec.url}" aria-label="${escHtml(rec.title)}">
            ${imgSrc ? `<img src="${imgSrc}" alt="${escHtml(rec.title)}" width="72" height="72" loading="lazy">` : ''}
            <span class="cart-empty-rec__name">${escHtml(rec.title)}</span>
            <span class="cart-empty-rec__price">${VixoKart.formatMoney(rec.variants[0].price)}</span>
          </a>
        `;
      }).join('');
      emptyRecs?.classList.remove('hidden');
    } catch {
      emptyRecs?.classList.add('hidden');
    }
  }

  // ─── Cart page upsell ─────────────────────────────────────────────────────────

  async function renderCartPageUpsell(cart) {
    const grid = document.getElementById('cart-page-upsell-grid');
    if (!grid || !cart.items.length) return;

    const lastItem       = cart.items[cart.items.length - 1];
    const cartProductIds = new Set(cart.items.map(i => i.product_id));

    try {
      const url  = `${window.routes.root_url || '/'}recommendations/products.json?product_id=${lastItem.product_id}&limit=8&intent=related`;
      const res  = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();
      const recs = (data.products || [])
        .filter(p => !cartProductIds.has(p.id) && p.available)
        .slice(0, 4);

      if (!recs.length) {
        const section = document.getElementById('cart-page-upsell');
        if (section) section.style.display = 'none';
        return;
      }

      grid.innerHTML = recs.map(rec => {
        const variant  = rec.variants[0];
        const imgSrc   = rec.featured_image
          ? rec.featured_image.split('?')[0] + '?width=300'
          : '';
        const onSale   = variant.compare_at_price && variant.compare_at_price > variant.price;
        return `
          <div class="cart-page-rec-card">
            <a href="${rec.url}" class="cart-page-rec-card__img-wrap" aria-label="${escHtml(rec.title)}">
              ${imgSrc
                ? `<img src="${imgSrc}" alt="${escHtml(rec.title)}" width="200" height="200" loading="lazy">`
                : `<div class="cart-page-rec-card__placeholder"></div>`
              }
              ${rec.available
                ? `<button
                     class="product-card__plus-add cart-rec-add"
                     data-quick-add-cart
                     data-variant-id="${variant.id}"
                     type="button"
                     aria-label="Add ${escHtml(rec.title)} to cart"
                   >
                     <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                       <line x1="8" y1="2" x2="8" y2="14" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
                       <line x1="2" y1="8" x2="14" y2="8" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
                     </svg>
                   </button>`
                : ''
              }
            </a>
            <div class="cart-page-rec-card__body">
              <a class="cart-page-rec-card__title" href="${rec.url}">${escHtml(rec.title)}</a>
              <div class="cart-page-rec-card__price">
                ${onSale
                  ? `<s class="muted" style="font-size:.8125rem;">${VixoKart.formatMoney(variant.compare_at_price)}</s>
                     <span style="color:var(--color-sale);font-weight:700;">${VixoKart.formatMoney(variant.price)}</span>`
                  : `<span style="font-weight:700;">${VixoKart.formatMoney(variant.price)}</span>`
                }
              </div>
            </div>
          </div>
        `;
      }).join('');

      // Bind quick add on cart page upsell cards
      grid.querySelectorAll('[data-quick-add-cart]').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();
          const variantId = btn.dataset.variantId;
          btn.classList.add('loading');
          try {
            await VixoKart.fetchJSON(window.routes.cart_add_url + '.js', {
              method: 'POST',
              body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
            });
            VixoKart.toast('Added to cart!', 'success');
            // Reload page so totals update
            window.location.reload();
          } catch {
            btn.classList.remove('loading');
            VixoKart.toast('Could not add item.', 'error');
          }
        });
      });

    } catch (err) {
      console.error('[VixoKart Cart] cart page upsell error:', err);
    }
  }

  // ─── API helper ───────────────────────────────────────────────────────────────

  async function changeItem(key, quantity) {
    const data = await VixoKart.fetchJSON(window.routes.cart_change_url + '.js', {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity }),
    });
    cartData = data;
    renderCartItems(data);
    renderSubtotal(data);
    renderCartCount(data);
    renderFreeShipping(data);
    return data;
  }

  // ─── Discount code ────────────────────────────────────────────────────────────

  async function applyDiscount() {
    const input = drawer.querySelector('#cart-discount-input');
    if (!input) return;
    const code = input.value.trim();
    if (!code) return;

    try {
      await fetch('/discount/' + encodeURIComponent(code), { redirect: 'manual' });
      VixoKart.toast('Discount code applied!', 'success');
      input.value = '';
    } catch {
      VixoKart.toast('Could not apply discount code.', 'error');
    }
  }

  // ─── Refresh ─────────────────────────────────────────────────────────────────

  async function refresh() {
    const cart = await fetchCart();
    renderCartItems(cart);
    renderSubtotal(cart);
    renderCartCount(cart);
    renderFreeShipping(cart);
    renderCartPageUpsell(cart);
    return cart;
  }

  // ─── Utility ─────────────────────────────────────────────────────────────────

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ─── Swipe-to-close (mobile bottom sheet, ≤480px) ───────────────────────────

  (function initSwipeToClose() {
    let startY   = 0;
    let currentY = 0;
    let dragging = false;
    let bodyScrollTop = 0;

    const THRESHOLD = 0.35; // 35% of drawer height triggers close
    const MIN_DRAG  = 8;    // px minimum movement before committing to a drag

    function isMobileSheet() {
      return window.matchMedia('(max-width: 480px)').matches;
    }

    drawer.addEventListener('touchstart', (e) => {
      if (!isMobileSheet() || !drawer.classList.contains('active')) return;
      startY        = e.touches[0].clientY;
      currentY      = startY;
      dragging      = false;
      bodyScrollTop = body ? body.scrollTop : 0;
    }, { passive: true });

    drawer.addEventListener('touchmove', (e) => {
      if (!isMobileSheet() || !drawer.classList.contains('active')) return;

      const deltaY = e.touches[0].clientY - startY;
      currentY     = e.touches[0].clientY;

      // Only intercept downward swipes
      if (deltaY <= 0) return;

      // If cart body is scrolled down, let native scroll handle it
      if (!dragging && bodyScrollTop > 0) return;

      // Wait for minimum movement before committing (avoids accidental drags)
      if (!dragging && deltaY < MIN_DRAG) return;
      dragging = true;

      // Prevent page scroll while dragging the drawer down
      e.preventDefault();

      // Move drawer without CSS transition so it follows the finger exactly
      drawer.style.transition = 'none';
      drawer.style.transform  = `translateY(${deltaY}px)`;
    }, { passive: false });

    drawer.addEventListener('touchend', () => {
      if (!isMobileSheet() || !dragging) {
        dragging = false;
        return;
      }
      dragging = false;

      const deltaY    = currentY - startY;
      const threshold = drawer.offsetHeight * THRESHOLD;

      // Restore CSS transition so the snap/close animates smoothly
      drawer.style.transition = '';

      if (deltaY > threshold) {
        // Past threshold — close the drawer (CSS will animate translateY(100%))
        drawer.style.transform = '';
        close();
      } else {
        // Below threshold — snap back to fully open
        drawer.style.transform = 'translateY(0)';
        drawer.addEventListener('transitionend', () => {
          // Let CSS own the transform again once the snap animation finishes
          if (drawer.classList.contains('active')) drawer.style.transform = '';
        }, { once: true });
      }
    }, { passive: true });
  })();

  // ─── Event bindings ───────────────────────────────────────────────────────────

  // Open cart
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-cart]')) {
      e.preventDefault();
      open();
    }
  });

  // Close cart
  drawer.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-close]')) close();
  });
  overlay?.addEventListener('click', close);

  // ESC key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('active')) close();
  });

  // Checkout button — call close() first so the header/body/nav state is fully
  // restored BEFORE navigation. Without this, the header stays hidden+inert and
  // when the user presses Back the whole page appears broken (bfcache restores
  // it exactly as it was at navigation time).
  drawer.querySelector('#cart-checkout-btn')?.addEventListener('click', function(e) {
    e.preventDefault();
    e.stopPropagation();
    close();
    window.location.href = '/checkout';
  });

  // Bfcache safety net — if the user navigates back from /checkout the page is
  // restored from cache. Make sure the drawer is fully closed and the header is
  // visible regardless of what state it was in when we navigated away.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      close();
    }
  });

  // Discount code
  drawer.querySelector('[data-apply-discount]')?.addEventListener('click', applyDiscount);
  drawer.querySelector('#cart-discount-input')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); applyDiscount(); }
  });

  // ─── Init ─────────────────────────────────────────────────────────────────────

  refresh();

  return { open, close, refresh, fetchCart };

})();
